// ─────────────────────────────────────────────────────────────────────────────
// FOCUS_RING — the one focus contract.
//
// There were five dialects: `ring-2 + ring-offset-2`, `ring-2 + ring-offset-0`,
// `ring-1 + ring-ring`, a native `outline-2 + outline-ring/70`, and a family of
// bespoke `focus:` (not `focus-visible:`) border and background changes with no
// ring at all. They disagreed about width, about the gap, about the colour
// token and about whether a mouse click should light the control up.
//
// One spelling, everywhere:
//
//     focus-visible:outline-hidden
//     focus-visible:ring-2 focus-visible:ring-ring
//     focus-visible:ring-offset-1
//
// Two things about this project make the details load-bearing rather than
// arbitrary, and both were verified rather than assumed:
//
//   1. `--ring` is byte-identical to `--primary`. A ring drawn INSIDE a filled
//      primary button is therefore the same colour as the button and vanishes.
//      A Tailwind `ring` is painted outside the border box, so the contract
//      works on filled variants for the same reason it works on transparent
//      ones, and no variant needs its own focus rule.
//
//   2. `--tw-ring-offset-color` used to be pinned to `--bg-canvas`, which is
//      right only for a control sitting directly on the page ground. On a card
//      or inside a dialog it painted a canvas-coloured band over a surface that
//      is not canvas. It is `transparent` now (see globals.css), so the offset
//      is a real gap showing whatever the control actually sits on, at any rung
//      of the ladder and in either theme.
//
// The gap is 1px, not 2px. Two is a visible halo on a 32px control and starts
// to read as the glow this phase is not allowed to have; one is the hairline
// gap that keeps the ring off the control's own border.
//
// `focus-visible`, never `focus`: a ring on every mouse click is noise, and the
// bespoke `focus:` sites were lighting up on pointer-down.
// ─────────────────────────────────────────────────────────────────────────────
export const FOCUS_RING =
  "focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1";

// ─────────────────────────────────────────────────────────────────────────────
// DISABLED — the one disabled treatment.
//
// There were six opacities: 15, 30, 40, 50 and 70, plus a bare
// `pointer-events-none` with no opacity at all. Fifty is the one, and it is the
// value the five shared primitives already carried, so the majority of call
// sites did not move.
//
// `cursor-not-allowed` is paired with it rather than `pointer-events-none`,
// because the two are mutually exclusive by construction: an element with no
// pointer events never shows a cursor. Buttons add `pointer-events-none`
// themselves, on top, since a disabled button must also refuse its hover.
// ─────────────────────────────────────────────────────────────────────────────
export const DISABLED = "disabled:cursor-not-allowed disabled:opacity-50";
